/* eslint-disable @typescript-eslint/no-empty-object-type */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Type,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core'
import { MatDrawer } from '@angular/material/sidenav'
import { NavigationEnd, Router } from '@angular/router'
import { AuctionStatusEnum } from '@galabid/gb-dashboard/auctions/data-access'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { toggleNavBarVisibility } from 'libs/gb-dashboard/core/util/src/lib/navbar/navbar-utils'
import { IconLibrary } from 'libs/gb-dashboard/shared/ui/src/lib/gb-icon/gb-icon-library.models'
import { GbBreakpointService } from 'libs/gb-dashboard/shared/utils/src/lib/gb-breakpoint-service/gb-breakpoint.service'
import { combineLatest, combineLatestWith, filter, map, Observable, of, take } from 'rxjs'
import { GbSidenavVisibilityService } from './gb-sidenav-visibility.service'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { GbSetupGuideProgressService } from 'libs/gb-dashboard/setup-guide/utils/src/lib/gb-setup-guide-progress.service'

export interface GbSidenavItem {
  isDivider: boolean
  icon?: string
  iconLibrary?: IconLibrary.ionic | IconLibrary.material
  label?: string
  routerLink?: string | null
  parentRouterLink?: string | null
  subItems?: GbSidenavItem[] | null
  expanded?: boolean
  backButton?: boolean
  badgeText$?: Observable<string>
  shouldShowBadge$?: Observable<boolean>
  hints$?: Observable<number>
  hintsOptional$?: Observable<boolean>
  highlight$?: Observable<boolean>
  onClick?: (event: MouseEvent) => void
  additionalActiveRoute?: string[]
  hintHoverText?: string
  hintHoverText$?: Observable<string>
  additionalLabelText$?: Observable<string | undefined>
  params?: {}
  dynamicComponent?: Type<any>
}

@UntilDestroy()
@Component({
  selector: 'gb-dashboard-sidenav',
  templateUrl: './gb-dashboard-sidenav.component.html',
  styleUrls: ['./gb-dashboard-sidenav.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class GbDashboardSidenavComponent implements OnInit, OnDestroy {
  @Input() sidenavItems$!: Observable<GbSidenavItem[]>
  @Input() collapseMenuButtonColor!: string

  gbBreakpointService = inject(GbBreakpointService)
  gbSidenavVisibilityService = inject(GbSidenavVisibilityService)
  selectedAuctionFacade = inject(SelectedAuctionFacade)
  setupGuideProgressService = inject(GbSetupGuideProgressService)
  router = inject(Router)
  cdr = inject(ChangeDetectorRef)

  IconLibrary = IconLibrary

  collapseMenu$: Observable<boolean> = this.gbBreakpointService.desktopView$.pipe(map((desktopView) => !desktopView))

  mode$: Observable<'over' | 'push'> = this.collapseMenu$.pipe(
    map((smBreakpoint) => {
      return smBreakpoint ? 'over' : 'push'
    }),
  )

  auctionStatus$ = this.selectedAuctionFacade.auction$.pipe(map((auction) => auction?.status))
  shouldHideSetupGuide$ = this.setupGuideProgressService.shouldHideSetupGuide$
  isAuctionHidden$ = this.selectedAuctionFacade.isAuctionHidden$
  setupGuideDrawerOpened$ = this.setupGuideProgressService.setupGuideDrawerOpened$
  desktopView$ = this.gbBreakpointService.desktopView$
  readonly setupGuideDrawerMinWidthPx = 300
  readonly setupGuideDrawerMaxWidthPx = Math.round(this.setupGuideDrawerMinWidthPx * 1.5)
  setupGuideDrawerWidthPx = this.setupGuideDrawerMinWidthPx
  isSetupGuideDrawerExpanded = false

  menuButtonVisible = true
  private isSetupGuideResizing = false

  @ViewChild('drawer') drawer!: MatDrawer
  @ViewChild('setupGuideDrawer') setupGuideDrawer!: MatDrawer

  ngOnInit(): void {
    this.checkActiveRoute()

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.checkActiveRoute()
      })
  }

  getItemClass(sidenavItem: GbSidenavItem): Observable<string[]> {
    if (sidenavItem.backButton) {
      return of(['back-button'])
    }

    if (!sidenavItem.highlight$) {
      return of(['normal'])
    }

    return sidenavItem.highlight$.pipe(map((highlight) => (highlight ? ['highlight', 'normal'] : ['normal'])))
  }

  ngOnDestroy(): void {
    this.gbSidenavVisibilityService.onHideSidenav()
  }

  getParentSidenavItemHints$(sidenavItem: GbSidenavItem): Observable<number> {
    const hintsObsList: Observable<number>[] = [sidenavItem.hints$ || of(0)]
    sidenavItem.subItems?.forEach((subItem) => {
      hintsObsList.push(subItem.hints$ || of(0))
    })
    return combineLatest(hintsObsList).pipe(map((hints) => hints.reduce((acc, hint) => acc + hint, 0)))
  }

  getParentSidenavHintOptional$(sideNavItem: GbSidenavItem): Observable<boolean> {
    const parentOptional$ = sideNavItem.hintsOptional$ ? sideNavItem.hintsOptional$ : of(false)

    const hintsObsList: Observable<{ qty: number; optional: boolean }>[] = []

    sideNavItem.subItems?.forEach((subItem) => {
      if (subItem?.hints$) {
        const optional$ = subItem.hintsOptional$ ? subItem.hintsOptional$ : of(false)
        hintsObsList.push(
          subItem.hints$.pipe(
            combineLatestWith(optional$),
            map(([qty, optional]) => ({ qty, optional })),
          ),
        )
      }
    })

    if (hintsObsList.length === 0) {
      return parentOptional$
    }

    return combineLatest([parentOptional$, combineLatest(hintsObsList)]).pipe(
      map(
        ([parentOptional, hintsList]) =>
          parentOptional || !hintsList.find((hint) => hint.qty && hint.optional === false),
      ),
    )
  }

  ngAfterViewInit() {
    this.monitorSidenavVisibility()
  }

  onSetupGuideResizeStart(event: PointerEvent): void {
    event.preventDefault()
    this.isSetupGuideResizing = true
  }

  @HostListener('document:pointermove', ['$event'])
  onSetupGuideResize(event: PointerEvent): void {
    if (!this.isSetupGuideResizing || !this.setupGuideDrawer?.opened) {
      return
    }

    const nextWidth = Math.min(
      this.setupGuideDrawerMaxWidthPx,
      Math.max(this.setupGuideDrawerMinWidthPx, window.innerWidth - event.clientX),
    )

    if (nextWidth === this.setupGuideDrawerWidthPx) {
      return
    }

    const wasSetupGuideDrawerExpanded = this.isSetupGuideDrawerExpanded
    const isSetupGuideDrawerExpanded = nextWidth > this.setupGuideDrawerMinWidthPx

    this.setupGuideDrawerWidthPx = nextWidth
    this.isSetupGuideDrawerExpanded = isSetupGuideDrawerExpanded

    if (!wasSetupGuideDrawerExpanded && isSetupGuideDrawerExpanded && this.drawer?.opened) {
      this.drawer.close()
    }

    this.cdr.markForCheck()
  }

  @HostListener('document:pointerup')
  @HostListener('document:pointercancel')
  onSetupGuideResizeEnd(): void {
    if (!this.isSetupGuideResizing) {
      return
    }

    this.isSetupGuideResizing = false
  }

  monitorSidenavVisibility(): void {
    this.drawer.openedStart.pipe(untilDestroyed(this)).subscribe(() => {
      this.gbSidenavVisibilityService.onShowSidenav()
    })
    this.drawer.closedStart.pipe(untilDestroyed(this)).subscribe(() => {
      this.gbSidenavVisibilityService.onHideSidenav()
    })
  }

  onClosedStart() {
    toggleNavBarVisibility(true)
  }
  onOpenedStart() {
    toggleNavBarVisibility(false)
  }

  hideMenuOnMobile(desktopView: boolean | null, sidenavItem: GbSidenavItem): void {
    if (!desktopView && !this.hasSubItems(sidenavItem) && this.drawer) {
      this.drawer.close()
    }
  }

  getRouterLink(sidenavItem: GbSidenavItem): string | null | undefined {
    return sidenavItem.routerLink || undefined
  }

  hasSubItems(sidenavItem: GbSidenavItem): boolean {
    return !!(sidenavItem.subItems && sidenavItem.subItems.length > 0)
  }

  toggle(sideNavItem: GbSidenavItem) {
    if (sideNavItem.subItems) {
      sideNavItem.expanded = !sideNavItem.expanded

      if (sideNavItem.expanded) {
        this.collapseAllOtherMenuItems(sideNavItem)
      }
    }
  }

  collapseAllOtherMenuItems(ignoreCollapse: GbSidenavItem) {
    this.sidenavItems$.pipe(take(1)).subscribe((sidenavItems) => {
      sidenavItems.forEach((sidenavItem) => {
        if (sidenavItem === ignoreCollapse) {
          return
        }
        if (sidenavItem.subItems) {
          sidenavItem.expanded = false
        }
      })
    })
  }

  getClass(sideNavItem: GbSidenavItem): string {
    return sideNavItem.expanded && this.hasSubItems(sideNavItem) ? 'expanded' : 'collapsed'
  }

  onClick(sideNavItem: GbSidenavItem, event: MouseEvent) {
    if (sideNavItem.onClick) {
      sideNavItem.onClick(event)
    }
  }

  getSubItemRouterLink(sideNavItem: GbSidenavItem): string | undefined {
    if (!sideNavItem.routerLink) {
      return undefined
    }
    if (sideNavItem.parentRouterLink) {
      return `${sideNavItem.parentRouterLink}/${sideNavItem.routerLink}`
    }
    return sideNavItem.routerLink
  }

  checkActiveRoute() {
    const splitUrl = this.router.url.split(/[/?]/)

    this.sidenavItems$.pipe(take(1)).subscribe((sidenavItems) => {
      sidenavItems.forEach((sidenavItem) => {
        if (sidenavItem.subItems) {
          const isActive = sidenavItem.subItems.some((subItem) => {
            const isMainRouteActive = subItem.routerLink && splitUrl.includes(subItem.routerLink)
            const isAdditionalRouteActive = subItem.additionalActiveRoute?.some((additionalRoute) =>
              splitUrl.includes(additionalRoute),
            )
            return isMainRouteActive || isAdditionalRouteActive
          })

          sidenavItem.expanded = isActive
        }
      })

      this.cdr.detectChanges()
    })
  }

  getChipTextColor(status?: string | null, isHidden?: boolean | null) {
    if (this.collapseMenuButtonColor) {
      return this.collapseMenuButtonColor
    }
    if (!status || isHidden) {
      return 'var(--gb-primary-color-500-contrast)'
    }
    switch (status) {
      case AuctionStatusEnum.Stopped:
        return 'var(--gb-warning-color-500-contrast)'
      case AuctionStatusEnum.Paused:
        return 'var(--gb-highlight-color-contrast)'
      case AuctionStatusEnum.Running:
        return 'var(--gb-primary-color-500-contrast)'
      case AuctionStatusEnum.Closing:
        return 'var(--gb-warning-color-500-contrast)'
      default:
        return 'var(--gb-primary-color-500-contrast)'
    }
  }
}
